import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAdjustStock } from './inventoryApi';
import { STOCK_MOVEMENT_TYPE, type ItemStockDto, type StockMovementType } from './types';

interface Props {
  stock: ItemStockDto | null;
  opened: boolean;
  onClose: () => void;
}

// Movement types the backoffice operator picks from. Sale / SaleVoid /
// GRN / Transfer are server-driven and never appear here.
const SELECTABLE_TYPES: { value: string; key: string }[] = [
  { value: String(STOCK_MOVEMENT_TYPE.OPENING_COUNT), key: 'inventory.types.openingCount' },
  { value: String(STOCK_MOVEMENT_TYPE.RECOUNT),       key: 'inventory.types.recount' },
  { value: String(STOCK_MOVEMENT_TYPE.WASTAGE),       key: 'inventory.types.wastage' },
  { value: String(STOCK_MOVEMENT_TYPE.MANUAL_ADJUST), key: 'inventory.types.manualAdjust' },
];

// Types that require a note (the backend enforces this; the UI surfaces
// it ahead of time so the operator doesn't get a round-trip rejection).
const REQUIRES_NOTE: Set<StockMovementType> = new Set([
  STOCK_MOVEMENT_TYPE.WASTAGE,
  STOCK_MOVEMENT_TYPE.MANUAL_ADJUST,
]);

export function AdjustStockModal({ stock, opened, onClose }: Props) {
  const { t } = useTranslation();
  const adjust = useAdjustStock(stock?.branchId ?? '');
  const [type, setType] = useState<StockMovementType>(STOCK_MOVEMENT_TYPE.OPENING_COUNT);
  const [delta, setDelta] = useState<number | ''>(0);
  const [note, setNote] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setType(STOCK_MOVEMENT_TYPE.OPENING_COUNT);
      setDelta(0);
      setNote('');
      setBatchNumber('');
      setExpiryDate(null);
      setError(null);
    }
  }, [opened]);

  if (!stock) return null;

  const noteRequired = REQUIRES_NOTE.has(type);
  const numericDelta = typeof delta === 'number' ? delta : 0;
  const valid = numericDelta !== 0 && (!noteRequired || note.trim().length > 0);

  const submit = async () => {
    setError(null);
    try {
      await adjust.mutateAsync({
        branchId: stock.branchId,
        itemId: stock.itemId,
        type,
        quantityDelta: numericDelta,
        note: note.trim() || null,
        batchNumber: batchNumber.trim() || null,
        expiryDate: expiryDate ? toDateOnlyString(expiryDate) : null,
      });
      notifications.show({ color: 'green', title: t('inventory.adjust.success'), message: stock.itemNameAr });
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail ?? (err instanceof Error ? err.message : t('inventory.adjust.failed')));
    }
  };

  // Batch / expiry are only meaningful for inbound (positive delta).
  // Outbound (negative) on this endpoint is wastage / shrink, which
  // doesn't carry a new batch — Phase 4F-2 would attribute the
  // outbound to a specific batch when FIFO lands.
  const showBatchFields = numericDelta > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600}>
          {t('inventory.adjust.title')} — {stock.itemNameAr}
        </Text>
      }
      centered
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('inventory.adjust.currentOnHand')}: <span className="font-numeric">{stock.onHand}</span>
        </Text>

        <Select
          label={t('inventory.adjust.type')}
          value={String(type)}
          onChange={(v) => v && setType(Number(v) as StockMovementType)}
          data={SELECTABLE_TYPES.map((opt) => ({ value: opt.value, label: t(opt.key) }))}
          allowDeselect={false}
        />

        <NumberInput
          label={t('inventory.adjust.delta')}
          description={t('inventory.adjust.deltaHint')}
          value={delta}
          onChange={(v) => setDelta(typeof v === 'string' ? Number(v) : v)}
          decimalScale={3}
          required
        />

        {showBatchFields && (
          <Group grow align="flex-start">
            <TextInput
              label={t('inventory.adjust.batchLabel')}
              description={t('inventory.adjust.batchHelp')}
              placeholder="L-2026-05-15"
              value={batchNumber}
              onChange={(e) => setBatchNumber(e.currentTarget.value)}
            />
            <DatePickerInput
              label={t('inventory.adjust.expiryLabel')}
              description={t('inventory.adjust.expiryHelp')}
              value={expiryDate}
              onChange={(v) => setExpiryDate(v)}
              clearable
              minDate={new Date()}
            />
          </Group>
        )}

        <Textarea
          label={t('inventory.adjust.note')}
          description={noteRequired ? t('inventory.adjust.noteRequired') : undefined}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          minRows={2}
          required={noteRequired}
        />

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} stroke={1.6} />}>
            {error}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={adjust.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            color="wheels"
            onClick={submit}
            loading={adjust.isPending}
            disabled={!valid || adjust.isPending}
          >
            {t('inventory.adjust.apply')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Format a JS Date as "YYYY-MM-DD" for the server's DateOnly column.
 *  Uses local-date components so a Date picked in the operator's
 *  timezone lands on the same calendar day they meant. */
function toDateOnlyString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
